if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 100));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Thumbnail Carousel navigation arrows setup
        const thumbTrack = this.elements.thumbnails.querySelector('.pdp-thumb-track');
        const prevBtn = this.elements.thumbnails.querySelector('.pdp-thumb-arrow--prev');
        const nextBtn = this.elements.thumbnails.querySelector('.pdp-thumb-arrow--next');

        if (thumbTrack && prevBtn && nextBtn) {
          const updateArrows = () => {
            const scrollLeft = Math.ceil(thumbTrack.scrollLeft);
            const maxScroll = thumbTrack.scrollWidth - thumbTrack.clientWidth;

            if (maxScroll <= 3) {
              prevBtn.classList.add('is-disabled');
              nextBtn.classList.add('is-disabled');
              return;
            }

            if (scrollLeft <= 5) {
              prevBtn.classList.add('is-disabled');
            } else {
              prevBtn.classList.remove('is-disabled');
            }

            if (scrollLeft >= maxScroll - 5) {
              nextBtn.classList.add('is-disabled');
            } else {
              nextBtn.classList.remove('is-disabled');
            }
          };

          prevBtn.addEventListener('click', (e) => {
            e.preventDefault();
            thumbTrack.scrollBy({ left: -240, behavior: 'smooth' });
          });

          nextBtn.addEventListener('click', (e) => {
            e.preventDefault();
            thumbTrack.scrollBy({ left: 240, behavior: 'smooth' });
          });

          thumbTrack.addEventListener('scroll', updateArrows, { passive: true });
          window.addEventListener('resize', updateArrows, { passive: true });
          setTimeout(updateArrows, 100);
          setTimeout(updateArrows, 500);
        }

        // Mobile swipe handling for main product gallery
        let touchStartX = 0;
        let touchEndX = 0;

        this.elements.viewer.addEventListener('touchstart', (e) => {
          touchStartX = e.changedTouches[0].screenX;
        }, { passive: true });

        this.elements.viewer.addEventListener('touchend', (e) => {
          touchEndX = e.changedTouches[0].screenX;
          const diff = touchStartX - touchEndX;
          const threshold = 40; // minimum distance in px to trigger slide
          
          if (Math.abs(diff) > threshold) {
            const nextButton = this.elements.viewer.querySelector('button[name="next"]');
            const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
            
            if (diff > 0 && nextButton && !nextButton.hasAttribute('disabled')) {
              nextButton.click();
            } else if (diff < 0 && prevButton && !prevButton.hasAttribute('disabled')) {
              prevButton.click();
            }
          }
        }, { passive: true });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const scrollPadding = parseFloat(getComputedStyle(activeMedia.parentElement).scrollPaddingLeft) || 0;
            const scrollTarget = activeMedia.offsetLeft - scrollPadding;
            activeMedia.parentElement.scrollTo({ left: scrollTarget });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        if (activeThumbnail && activeThumbnail.dataset.mediaPosition) {
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => {
            element.removeAttribute('aria-current');
            element.classList.remove('active');
          });
        
        const btn = thumbnail.querySelector('button');
        if (btn) {
          btn.setAttribute('aria-current', 'true');
          btn.classList.add('active');
        }

        thumbnail.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
